import {
  ChartConfig,
  ChartContainer,
  cn,
  getPieMinAngle,
} from "web-design-system";
import { Pie, PieChart, Cell } from "recharts";

const FUND_CATEGORY_COLORS = {
  Equity: "var(--chart-1)",
  Debt: "var(--chart-2)",
  Hybrid: "var(--chart-3)",
  Commodity: "var(--chart-4)",
};

const FUND_SUB_CATEGORY_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
  "var(--color-violet-500)",
  "var(--color-yellow-200)",
];

export const CORPUS_BREAKDOWN_COLORS = {
  linkedHoldings: "var(--chart-1)",
  oneTime: "var(--chart-2)",
  sip: "var(--chart-3)",
  returns: "var(--chart-4)",
} as const;

export type ChartDataItem = { name: string; value: number; fill: string };

export function buildBreakdownData(
  data: { category: string; percentage: number }[] | undefined,
  colorMap?: Record<string, string>
): ChartDataItem[] {
  if (!data || data.length === 0) return [];
  return data.map((item, index) => ({
    name: item.category,
    value: item.percentage,
    fill:
      colorMap?.[item.category] ??
      FUND_SUB_CATEGORY_COLORS[index % FUND_SUB_CATEGORY_COLORS.length],
  }));
}

export function buildCategoryBreakdownData(
  data: { category: string; percentage: number }[] | undefined
): ChartDataItem[] {
  return buildBreakdownData(data, FUND_CATEGORY_COLORS);
}

export function buildFundLevelBreakdownData(
  data: { category: string; percentage: number }[] | undefined
): ChartDataItem[] {
  if (!data || data.length === 0) return [];
  const sorted = [...data].sort((a, b) => b.percentage - a.percentage);
  const top6 = sorted.slice(0, 6);
  const rest = sorted.slice(6);
  const result = top6.map((item, index) => ({
    name: item.category,
    value: item.percentage,
    fill: FUND_SUB_CATEGORY_COLORS[index],
  }));
  if (rest.length > 0) {
    const othersTotal = rest.reduce((sum, item) => sum + item.percentage, 0);
    result.push({
      name: "Others",
      value: Math.round(othersTotal * 100) / 100,
      fill: FUND_SUB_CATEGORY_COLORS[6],
    });
  }
  return result;
}

export function buildChartConfig(
  chartData: { name: string; fill: string }[]
): ChartConfig {
  const config: ChartConfig = { value: { label: "Allocation %" } };
  chartData.forEach((item) => {
    config[item.name] = { label: item.name, color: item.fill };
  });
  return config;
}

export function BreakdownPieChart({
  title,
  data,
  className,
}: {
  title: string;
  data: ChartDataItem[];
  className?: string;
}) {
  if (data.length === 0) return null;
  const config = buildChartConfig(data);
  return (
    <div
      className={cn(
        "flex-1 bg-card border border-border p-6 flex flex-col rounded-lg",
        className
      )}
    >
      <span className="mb-4 text-base font-semibold">{title}</span>
      <div className="flex items-center justify-center">
        <ChartContainer
          config={config}
          className="w-full aspect-square max-h-100"
        >
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={80}
              outerRadius={120}
              paddingAngle={2}
              cornerRadius={4}
              minAngle={getPieMinAngle(80, 120)}
              label={({ value }) => `${value}%`}
              labelLine={true}
              isAnimationActive={false}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
          </PieChart>
        </ChartContainer>
      </div>
      <div className="flex flex-wrap gap-3 justify-center mt-4">
        {data.map((item) => (
          <div key={item.name} className="flex items-center gap-2">
            <div
              className="w-5 h-3 rounded"
              style={{ backgroundColor: item.fill }}
            />
            <span className="text-xs">{item.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
